import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import './profileCards.css';

/**
 * The top of the record column: who this is, and the two things people take
 * off a record.
 *
 * Never collapsible. Every card under it folds; this one does not, because
 * collapsing the name of the thing you are looking at is not a state anybody
 * wants to be in.
 *
 * ── What is deliberately not here yet ─────────────────────────────────────
 *
 * HubSpot puts a row of six identical icon buttons under the identity on every
 * record — Note, Email, Call, Task, Meeting, More — and the sameness is the
 * feature. Ours would be four dead buttons and two live ones: we have no call
 * logging, no meeting record, and sales tasks are Dev 2's build and have not
 * landed. Six buttons where four do nothing teaches people the row is
 * decoration, which costs more than the muscle memory it buys. The row goes in
 * when there is something behind it.
 */
export function ProfileIdentity({
  back,
  actions,
  name,
  role,
  children,
}: {
  /** The "< Leads" link back to the list this was opened from. */
  back?: ReactNode;
  /** The record's own menu, opposite the back link. */
  actions?: ReactNode;
  /**
   * The record's name, when nothing above this block already says it.
   *
   * Optional, and the reason is not laziness. A profile mounted in a drawer
   * sits under a header bar that already prints the name and is the drawer's
   * own aria-labelledby, so printing it again gives you the same words twice,
   * two <h2>s with identical text, and a screen reader offering a choice
   * nobody can make. A profile mounted as a page has no such bar and needs it.
   *
   * The rule: whoever owns the frame decides. Pass the name when the frame
   * does not have it.
   */
  name?: ReactNode;
  /** The line under the name: title, and where. */
  role?: ReactNode;
  /** Link rows. Email and phone, usually. */
  children?: ReactNode;
}) {
  return (
    <div className="profile-identity-block">
      {back === undefined && actions === undefined ? null : (
        <div className="profile-identity-top">
          <div>{back}</div>
          <div>{actions}</div>
        </div>
      )}
      {name === undefined ? null : <h2 className="profile-identity-name">{name}</h2>}
      {role === undefined ? null : <p className="profile-identity-role">{role}</p>}
      {children}
    </div>
  );
}

/**
 * One line of the identity block: a value, and a button that copies it.
 *
 * ── Why the copy button is not optional ───────────────────────────────────
 *
 * An email address on a record exists to be pasted somewhere else. Selecting
 * it by hand out of a 300px column that ellipsises it does not work, which is
 * why HubSpot puts a copy control beside both the email and the phone and why
 * this component has no variant without one.
 *
 * ── The failure it handles ────────────────────────────────────────────────
 *
 * navigator.clipboard does not exist outside a secure context and throws when
 * the document is not focused. A copy button that silently does nothing is
 * worse than none, because the person believes they have the address and
 * pastes whatever was on their clipboard already. On failure this says
 * "Copy failed" rather than "Copied", which is the smallest honest thing a
 * one-line control can do; CopyLinkButton, which has room, falls back to a
 * selectable field instead.
 */
export function ProfileLinkRow({
  href,
  value,
  label,
}: {
  /** Where the text goes. Without one it is plain text with a copy button. */
  href?: string | undefined;
  /** The text shown, and the exact string copied. */
  value: string;
  /** What this is, for the copy button's accessible name. */
  label: string;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A pending reset on an unmounted drawer would set state on a dead
  // component, and this drawer is closed fast and often.
  useEffect(() => {
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);

  // Whatever it was showing belonged to the old record. The profile swaps in
  // place when you click an association, so this is a real transition.
  useEffect(() => {
    setState('idle');
  }, [value]);

  async function copy(): Promise<void> {
    let ok = false;
    try {
      // Optional chaining, not truthiness: on plain http there is no
      // clipboard object at all, and reading .writeText off it would throw.
      if (navigator.clipboard?.writeText !== undefined) {
        await navigator.clipboard.writeText(value);
        ok = true;
      }
    } catch {
      ok = false;
    }
    setState(ok ? 'copied' : 'failed');
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 2500);
  }

  return (
    <div className="profile-identity-link">
      {href === undefined ? (
        <span>{value}</span>
      ) : (
        <a href={href} target="_blank" rel="noreferrer">
          {value}
        </a>
      )}
      <button
        type="button"
        className="profile-copy"
        aria-label={`Copy ${label}`}
        onClick={() => {
          void copy();
        }}
      >
        {state === 'idle' ? '⧉' : null}
        {/* The outcome is text, not a changed icon. A glyph that swaps to a
            tick says "something happened" and not "it worked", and the
            failure case is exactly the one that must not be ambiguous. */}
        {state === 'copied' ? <span className="profile-copy-said">Copied</span> : null}
        {state === 'failed' ? <span className="profile-copy-said">Copy failed</span> : null}
      </button>
    </div>
  );
}
